import { useEffect, useState } from 'react'
import Alert from '@mui/material/Alert'
import Typography from '@mui/material/Typography'
import DialogConfirmCancel from '../../../../../dialog/DialogConfirm'
import CardKeyValue from '../../../../../card/cardKeyValue'
import { useSelector } from 'react-redux'
import { Box, Divider, Grid } from '@mui/material'
import { capitalizarPrimeraLetra } from '../../../../../../utils'
import useAnalyticsCurriculum from '../../../../../../hooks/analytics/useAnalyticsCurriculum'
import SelectField from '../../../../../FormControl/input/SelectField'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import ChipStatus from '../Current/Elements/ChipStatus'
import InputField from '../../../../../FormControl/input/InputField'

const DialogAction = ({ show, onClose, onApply, type, formData = {} }) => {
  const types = ['batch-status', 'generate-data', 'edit-status']
  const status = useSelector(
    (state) => state?.analytics?.analyticsCurriculum?.datasets?.data?.data?.status || []
  )
  const curriculumId = useSelector(
    (state) => state?.analytics?.analyticsCurriculum?.datasets?.curriculum?._id
  )
  const [loading, toggleLoading] = useState(false)
  const [errors, setInputErrors] = useState({})
  const [form, setInputForm] = useState({ ...formData })
  const { updateStatusDevelopmentalRubricBatch, generateDataForAnalyticsCurriculum } =
    useAnalyticsCurriculum()

  const onChange = (name, value) => {
    setInputForm({ ...form, [name]: value })
    setInputErrors((old) => ({ ...old, [name]: null }))
  }

  const onConfirm = async (e) => {
    if (!types.includes(type)) {
      setInputErrors({ message: 'Invalid type' })
      return
    }
    if (form.ids === undefined) {
      setInputErrors({ message: 'Invalid id' })
      return
    }

    e.preventDefault()
    toggleLoading(true)
    try {
      let result = { error: false }
      if (type === 'batch-status') {
        result = await updateStatusDevelopmentalRubricBatch(curriculumId, form.ids, form.status)
      }
      if (type === 'edit-status') {
        result = await updateStatusDevelopmentalRubricBatch(curriculumId, [form._id], form.status)
      }
      if (type === 'generate-data') {
        result = await generateDataForAnalyticsCurriculum(form)
      }

      if (result.error) {
        setInputErrors(result)
      } else {
        onApply()
      }
    } finally {
      toggleLoading(false)
    }
  }

  useEffect(() => {
    setInputForm({ ...formData, ids: formData?.uls?.map((item) => item._id) || [] })
    if (formData.error) {
      setInputErrors({ message: formData.error })
    }
  }, [formData, type])

  return (
    <DialogConfirmCancel
      id={`class-${type}`}
      open={show}
      onCancel={onClose}
      onConfirm={types.includes(type) ? onConfirm : undefined}
      loading={loading}
      title={type === 'batch-status' ? 'Update status' : capitalizarPrimeraLetra(type.replace('-', ' '))}
      onCloseHeader={true}
      maxWidth={'xs'}
    >
      {types.includes(type) && (
        <>
          <>
            {['batch-status'].includes(type) && !formData.error && (
              <div className='flex flex-wrap gap-1 pb-4 items-center justify-center'>
                {form?.uls?.slice(0, 9).map((item, index) => (
                  <CardKeyValue
                    key={index}
                    title='Unit of learning'
                    className={`min-w-100px max-w-150px`}
                    value={<div>{item.name}</div>}
                  />
                ))}
                {form?.uls?.length > 10 && (
                  <CardKeyValue
                    title='MORE'
                    className={`min-w-100px max-w-150px`}
                    value={<div>+{form?.uls?.length - 10} more</div>}
                  />
                )}
              </div>
            )}
            {['edit-status'].includes(type) && !formData.error && (
              <div className='flex flex-wrap gap-1 pb-4 items-center justify-center'>
                <CardKeyValue
                  title='Unit of learning'
                  className={`min-w-100px max-w-150px`}
                  value={<div>{form.name}</div>}
                />
              </div>
            )}

            {['batch-status', 'edit-status'].includes(type) && !formData.error && (
              <Box className=''>
                <Grid container spacing={2} alignItems='center'>
                  <Grid item sm={3}>
                    <Typography
                      variant='inherit'
                      className='font-bold capitalize'
                      color='primary'
                      align='right'
                      component='p'
                    >
                      Status
                    </Typography>
                  </Grid>
                  <Grid item sm={9}>
                    <SelectField
                      id='status'
                      name='status'
                      value={form?.status || ''}
                      onChange={(e) => onChange('status', e.target.value)}
                      options={status.map((item) => ({
                        value: item.code,
                        label: (
                          <div className='flex gap-2 items-center'>
                            <ChipStatus
                              status={item.name}
                              color={item.color}
                              bgColor={item.bgColor}
                              border={item.border}
                              size='small'
                            />
                          </div>
                        )
                      }))}
                      placeholder='Choose a status'
                      error={errors?.status}
                      helperText={errors?.status}
                    />
                  </Grid>
                </Grid>
              </Box>
            )}
            {type === 'generate-data' && (
              <Box className=''>
                <Typography variant='inherit' className='text-lg' color='primary' component='p'>
                  Are you sure you want to generate data for this report?
                </Typography>
                <Divider className='my-2' />
                <Typography variant='inherit' className='text-sm' color='primary' component='p'>
                  Add a description for history report (optional)
                </Typography>
                <Grid container spacing={2} alignItems='center'>
                  <Grid item sm={3}>
                    <Typography
                      variant='inherit'
                      className='font-bold capitalize'
                      color='primary'
                      align='right'
                      component='p'
                    >
                      Description
                    </Typography>
                  </Grid>
                  <Grid item sm={9}>
                    <InputField
                      id='description'
                      name='description'
                      value={form?.description || ''}
                      onChange={(e) => onChange('description', e.target.value)}
                      placeholder='Enter a description'
                      error={errors?.description}
                      helperText={errors?.description}
                    />
                  </Grid>
                </Grid>
              </Box>
            )}
          </>
        </>
      )}
      {errors?.message && (
        <Alert className='mt-4' severity='error'>
          {errors?.message}
        </Alert>
      )}
    </DialogConfirmCancel>
  )
}

export default DialogAction
